import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtDateLong, fmtTime } from "../../lib/format";
import { useAuth } from "../../auth";
import { can, isSystemAdmin, permissionsOf } from "../../lib/permissions";
import { Spinner, EmptyState, Button, Field, Chip, Sheet } from "../../components/ui";
import { Icon } from "../../components/icons";

const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
const ACTIONS = { "role.create": "Perfil criado", "role.update": "Perfil alterado", "role.delete": "Perfil eliminado", "user.roles.update": "Perfis de membro alterados", "user.singer.update": "Condição de cantor alterada" };

function PermGroup({ group, selected, toggle, locked, allowed }) {
  return (
    <div className="card" style={{ padding: 12 }} data-testid={`perm-group-${slug(group.group)}`}>
      <div className="t-section" style={{ marginBottom: 8 }}>{group.group}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {group.items.map((it) => {
          const checked = it.base || selected.has(it.key);
          const disabled = locked || it.base || !allowed(it.key);
          return (
            <label key={it.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: disabled ? "default" : "pointer", opacity: disabled && !checked ? 0.5 : 1 }}>
              <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(it.key)} style={{ marginTop: 3, accentColor: "var(--brand-primary)" }} data-testid={`perm-${it.key}`} />
              <span style={{ flex: 1 }}>
                <span style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{it.name}</span>
                {it.base ? <span className="t-hint" style={{ fontSize: 11 }}> · base de todos</span> : null}
                <span className="t-hint" style={{ display: "block", fontSize: 12 }}>{it.description}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function RoleEditor({ role, catalog, onClose, onSaved }) {
  const { user } = useAuth();
  const isNew = !role.id;
  const locked = !!role.system;
  const mine = permissionsOf(user);
  const allowed = (k) => isSystemAdmin(user) || mine.has(k);
  const [name, setName] = useState(role.name || "");
  const [description, setDescription] = useState(role.description || "");
  const [selected, setSelected] = useState(new Set(role.permissions || []));
  const [err, setErr] = useState("");
  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const base = new Set(catalog.flatMap((g) => g.items.filter((i) => i.base).map((i) => i.key)));

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim(), permissions: [...selected].filter((k) => !base.has(k)) };
      if (isNew) body.key = slug(name);
      return api(isNew ? "/admin/roles" : `/admin/roles/${role.id}`, { method: isNew ? "POST" : "PUT", body: JSON.stringify(body) });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e.message),
  });
  const submit = () => { setErr(""); if (!name.trim()) return setErr("Indique o nome do perfil."); if (isNew && slug(name).length < 2) return setErr("O nome precisa de pelo menos 2 letras ou números."); save.mutate(); };

  return (
    <Sheet title={isNew ? "Novo perfil" : locked ? role.name : "Editar perfil"} onClose={onClose} testid="role-form" wide>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {locked ? <div className="card" style={{ padding: 12, fontSize: 13 }} data-testid="role-system-notice"><Icon name="key" size={14} /> Perfil de sistema protegido: não pode ser alterado nem eliminado.{role.system_key === "admin" ? " Inclui automaticamente todas as permissões." : ""}</div> : null}
        <Field label="NOME" value={name} onChange={(e) => setName(e.target.value)} disabled={locked} placeholder="Ex.: Produtor" maxLength={60} data-testid="role-name-input" />
        <Field label="DESCRIÇÃO" value={description} onChange={(e) => setDescription(e.target.value)} disabled={locked} placeholder="Para que serve este perfil" data-testid="role-description-input" />
        {!isSystemAdmin(user) && !locked ? <div className="t-hint" style={{ fontSize: 12 }}>Só pode conceder permissões que também tem.</div> : null}
        {catalog.map((g) => <PermGroup key={g.group} group={g} selected={role.system_key === "admin" ? new Set(g.items.map((i) => i.key)) : selected} toggle={toggle} locked={locked} allowed={allowed} />)}
        {err ? <div style={{ background: "var(--error)", color: "var(--on-error)", padding: 10, borderRadius: "var(--r-md)", fontSize: 13 }} data-testid="role-form-error">{err}</div> : null}
        {!locked ? <Button loading={save.isPending} onClick={submit} data-testid="role-save">{isNew ? "CRIAR PERFIL" : "GUARDAR"}</Button> : null}
      </div>
    </Sheet>
  );
}

function AuditList({ roles }) {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["roles-audit"], queryFn: () => api("/admin/roles/audit?limit=30") });
  const membersQ = useQuery({ queryKey: ["admin-members"], queryFn: () => api("/admin/members"), enabled: can(user, "members.view", "members.manage") });
  const person = (id) => (membersQ.data || []).find((m) => m.id === id)?.name;
  const roleName = (id) => roles.find((r) => r.id === id)?.name || "—";
  const names = (ids) => (ids || []).map(roleName).join(", ") || "—";
  if (q.isLoading) return <Spinner />;
  const items = q.data || [];
  if (!items.length) return <EmptyState icon="clipboard" text="Ainda não há alterações registadas." />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="roles-audit">
      {items.map((a) => (
        <div key={a.id} className="card" style={{ padding: 10, fontSize: 13 }} data-testid={`audit-${a.id}`}>
          <div style={{ fontFamily: "var(--f-medium)" }}>{ACTIONS[a.action] || a.action}{a.before?.name || a.after?.name ? ` · ${a.after?.name || a.before?.name}` : a.action.startsWith("user.") && (a.member_name || person(a.target_id)) ? ` · ${a.member_name || person(a.target_id)}` : ""}</div>
          {a.action === "user.roles.update" ? <div className="t-hint" style={{ fontSize: 12 }}>{names(a.before?.role_ids)} → {names(a.after?.role_ids)}</div> : null}
          <div className="t-hint" style={{ fontSize: 11 }}>{fmtDateLong(a.at)} · {fmtTime(a.at)}{a.actor_name || person(a.actor_id) ? ` · por ${a.actor_name || person(a.actor_id)}` : ""}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminRoles() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("roles");
  const rolesQ = useQuery({ queryKey: ["admin-roles"], queryFn: () => api("/admin/roles") });
  const catQ = useQuery({ queryKey: ["admin-permissions"], queryFn: () => api("/admin/permissions") });
  const roles = rolesQ.data || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-roles"] }); qc.invalidateQueries({ queryKey: ["roles-audit"] }); qc.invalidateQueries({ queryKey: ["admin-members"] }); };
  const del = useMutation({
    mutationFn: (id) => api(`/admin/roles/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
    onError: (e) => window.alert(e.message),
  });
  const remove = (r) => {
    if (r.members_count) return window.alert(`Não é possível eliminar "${r.name}": está atribuído a ${r.members_count} membro(s). Retire-o primeiro na gestão de membros.`);
    if (window.confirm(`Eliminar o perfil "${r.name}"?`)) del.mutate(r.id);
  };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Perfis e permissões</h1>
        {can(user, "roles.manage") ? <Button className="auto sm" icon="add" onClick={() => setEditing({})} data-testid="role-new-button">NOVO PERFIL</Button> : null}
      </div>
      <div className="page ulc-fade" style={{ maxWidth: 900 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <Chip label="PERFIS" active={tab === "roles"} onClick={() => setTab("roles")} data-testid="roles-tab" />
          <Chip label="HISTÓRICO" active={tab === "audit"} onClick={() => setTab("audit")} data-testid="roles-audit-tab" />
        </div>
        {tab === "audit" ? <AuditList roles={roles} /> : rolesQ.isLoading || catQ.isLoading ? <Spinner /> : rolesQ.isError ? (
          <EmptyState icon="alert-circle-outline" text={rolesQ.error.message} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {roles.map((r) => (
              <div key={r.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12 }} data-testid={`role-row-${r.key}`}>
                <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="key" size={18} color="var(--brand-primary)" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{r.name} {r.system ? <span className="pill todo" style={{ marginLeft: 6 }}>SISTEMA</span> : null}</div>
                  <div className="t-hint" style={{ fontSize: 12 }}>{r.description || "Sem descrição"} · {r.system_key === "admin" ? "todas as permissões" : `${(r.permissions || []).length} permissões`} · {r.members_count || 0} membro(s)</div>
                </div>
                <button className="icon-btn" onClick={() => setEditing(r)} data-testid={`role-edit-${r.key}`}><Icon name={r.system ? "chevron-forward" : "pencil"} size={16} /></button>
                {!r.system ? <button className="icon-btn" onClick={() => remove(r)} data-testid={`role-delete-${r.key}`}><Icon name="trash" size={16} color="var(--muted)" /></button> : null}
              </div>
            ))}
          </div>
        )}
      </div>
      {editing ? <RoleEditor role={editing} catalog={catQ.data || []} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} /> : null}
    </>
  );
}
